import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tent, Plus, Loader2, X, Lock, CheckCircle2, Pencil, Trash2, Award, ClipboardList } from "lucide-react";

const blankRes = { name: "", content: "", total: "", correct: "", wrong: "", blank: "" };

export default function MathCamp({ studentId, readOnly }) {
  const [camps, setCamps] = useState([]);
  const [campId, setCampId] = useState(null);
  const [prog, setProg] = useState({ resources: [], topics: [], kurs: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resModal, setResModal] = useState(null);
  const [kurModal, setKurModal] = useState(null);
  const [campModal, setCampModal] = useState(false);
  const [campForm, setCampForm] = useState({ name: "", exam: "TYT" });
  const [addForms, setAddForms] = useState({});
  const [examForms, setExamForms] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: p }] = await Promise.all([api.get("/camps"), api.get(`/camp-progress/${studentId}`)]);
      setCamps(c);
      setProg(p);
      setCampId((prev) => (prev && c.some((x) => x.id === prev) ? prev : c[0]?.id || null));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { if (studentId) load(); }, [load, studentId]);

  const camp = camps.find((c) => c.id === campId);
  const kurs = useMemo(() => [...(camp?.kurs || [])].sort((a, b) => Number(a.no) - Number(b.no)), [camp]);
  const topicDone = (kurNo, t) => prog.topics.some((x) => x.camp_id === campId && x.kur_no === kurNo && x.topic === t);
  const kurState = (kurNo) => prog.kurs.find((x) => x.camp_id === campId && x.kur_no === kurNo);
  const isUnlocked = (idx) => idx === 0 || !!kurState(kurs[idx - 1]?.no)?.passed;
  const kurResources = (kurNo) => prog.resources.filter((r) => r.camp_id === campId && r.kur_no === kurNo);
  const solved = (kurNo) => kurResources(kurNo).reduce((a, r) => a + Number(r.correct || 0) + Number(r.wrong || 0) + Number(r.blank || 0), 0);

  const toggleTopic = async (kurNo, t) => {
    if (readOnly) return;
    try {
      await api.put(`/camp-progress/${studentId}/topics`, { camp_id: campId, kur_no: kurNo, topic: t, done: !topicDone(kurNo, t) });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addResource = async (kurNo) => {
    const f = addForms[kurNo] || blankRes;
    if (!f.name.trim()) return toast.error("Kaynak adı zorunlu");
    setSaving(true);
    try {
      await api.post(`/camp-progress/${studentId}/resources`, {
        camp_id: campId, kur_no: kurNo, name: f.name.trim(), content: f.content.trim(),
        total: Number(f.total || 0), correct: Number(f.correct || 0), wrong: Number(f.wrong || 0), blank: Number(f.blank || 0),
      });
      setAddForms((p) => ({ ...p, [kurNo]: blankRes }));
      toast.success("Kaynak eklendi");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const saveResModal = async () => {
    const r = resModal;
    setSaving(true);
    try {
      await api.put(`/camp-progress/${studentId}/resources/${r.id}`, {
        name: r.name, content: r.content,
        total: Number(r.total || 0), correct: Number(r.correct || 0), wrong: Number(r.wrong || 0), blank: Number(r.blank || 0),
        homework: !!r.homework, homework_note: r.homework_note || "",
      });
      toast.success(r.homework ? "Kaynak güncellendi — ödev verildi" : "Kaynak güncellendi");
      setResModal(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const delResource = async (id) => {
    try {
      await api.delete(`/camp-progress/${studentId}/resources/${id}`);
      toast.success("Kaynak silindi");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveKurExam = async (kur) => {
    const f = examForms[kur.no] || {};
    const c = Number(f.correct || 0), w = Number(f.wrong || 0), b = Number(f.blank || 0);
    if (!c && !w && !b) return toast.error("Sınav sonucu girin (D/Y/B)");
    setSaving(true);
    try {
      await api.put(`/camp-progress/${studentId}/kurs`, {
        camp_id: campId, kur_no: kur.no, passed: !!kurState(kur.no)?.passed,
        exam_total: Number(kur.exam_questions || 0) || c + w + b, exam_correct: c, exam_wrong: w, exam_blank: b,
      });
      toast.success("Kur sınavı sonucu kaydedildi");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const togglePass = async (kur) => {
    const st = kurState(kur.no);
    try {
      await api.put(`/camp-progress/${studentId}/kurs`, { camp_id: campId, kur_no: kur.no, passed: !st?.passed });
      toast.success(st?.passed ? "Kur geçişi geri alındı" : `${kur.name} onaylandı — öğrenci bir sonraki kura geçebilir`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openKurEdit = (kur) => {
    const nextNo = (kurs[kurs.length - 1]?.no || 0) + 1;
    setKurModal(kur
      ? { ...kur, topicsText: (kur.topics || []).join("\n") }
      : { no: nextNo, name: `${nextNo}. Kur`, topicsText: "", target: 500, exam_name: `${nextNo}. Kur Sınavı`, exam_questions: 30, _new: true });
  };

  const saveKur = async () => {
    const k = kurModal;
    const topics = k.topicsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!topics.length) return toast.error("En az bir konu girin (her satıra bir konu)");
    const item = { no: Number(k.no), name: (k.name || "").trim() || `${k.no}. Kur`, topics, target: Number(k.target || 0), exam_name: (k.exam_name || "").trim() || `${k.no}. Kur Sınavı`, exam_questions: Number(k.exam_questions || 0) };
    const newKurs = k._new ? [...kurs, item] : kurs.map((x) => (Number(x.no) === Number(item.no) ? item : x));
    setSaving(true);
    try {
      await api.put(`/camps/${campId}`, { kurs: newKurs });
      toast.success("Kur kaydedildi");
      setKurModal(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const delKur = async (kurNo) => {
    if (!window.confirm(`${kurNo}. kuru silmek istediğinize emin misiniz? Öğrencilerin bu kurdaki kaynak/sınav kayıtları silinmez ama görünmez olur.`)) return;
    try {
      await api.put(`/camps/${campId}`, { kurs: kurs.filter((x) => Number(x.no) !== Number(kurNo)) });
      toast.success("Kur silindi");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createCamp = async () => {
    if (!campForm.name.trim()) return toast.error("Kamp adı zorunlu");
    setSaving(true);
    try {
      const { data } = await api.post("/camps", { name: campForm.name.trim(), exam: campForm.exam, kurs: [] });
      toast.success("Kamp oluşturuldu — şimdi kur ekleyin");
      setCampModal(false);
      setCampForm({ name: "", exam: "TYT" });
      setCampId(data.id);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="math-camp">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Tent size={20} className="text-indigo-600" /> Matematik Kampı</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Kur sistemi: her kurun konuları, kaynakları ve kur sonu sınavı vardır. Hedef soru sayısına ulaşıp kur sınavı girilince admin/koç onayıyla öğrenci bir sonraki kura geçer.
          </p>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCampModal(true)} data-testid="camp-new-btn"><Plus size={15} /><span className="ml-1">Yeni Kamp</span></Button>
            {camp && <Button onClick={() => openKurEdit(null)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="camp-add-kur-btn"><Plus size={15} /><span className="ml-1">Kur Ekle</span></Button>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="camp-selector">
        {camps.map((c) => (
          <button key={c.id} onClick={() => setCampId(c.id)} data-testid={`camp-tab-${c.id}`}
            className={`text-sm px-4 py-2 rounded-xl border font-semibold transition-colors ${campId === c.id ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : !camp ? (
        <div className="card-ta p-10 text-center text-slate-400">Kamp bulunamadı.</div>
      ) : kurs.length === 0 ? (
        <div className="card-ta p-10 text-center text-slate-400">
          Bu kampta henüz kur yok.{!readOnly && <> Sağ üstteki <b>"Kur Ekle"</b> butonuyla kurları ve konuları elle tanımlayın.</>}
        </div>
      ) : (
        <div className="space-y-4">
          {kurs.map((kur, idx) => {
            const st = kurState(kur.no);
            const locked = !isUnlocked(idx);
            const done = !!st?.passed;
            const sv = solved(kur.no);
            const tgt = Number(kur.target || 0);
            const pct = tgt ? Math.min(100, Math.round((sv / tgt) * 100)) : 0;
            const res = kurResources(kur.no);
            const ef = examForms[kur.no] || {};
            return (
              <div key={kur.no} className={`card-ta overflow-hidden ${locked ? "opacity-60" : ""} ${done ? "ring-2 ring-emerald-400" : ""}`} data-testid={`camp-kur-${kur.no}`}>
                <div className={`px-4 py-3 flex items-center justify-between flex-wrap gap-2 border-b ${done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {locked ? <Lock size={16} className="text-slate-400" /> : done ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Tent size={16} className="text-indigo-600" />}
                    <span className="font-display font-bold text-slate-900">{kur.name}</span>
                    {done && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white" data-testid={`camp-kur-passed-${kur.no}`}>GEÇTİ</span>}
                    {locked && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">Kilitli — önceki kur onaylanınca açılır</span>}
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openKurEdit(kur)} className="text-slate-400 hover:text-amber-600" title="Kuru düzenle" data-testid={`camp-kur-edit-${kur.no}`}><Pencil size={15} /></button>
                      <button onClick={() => delKur(kur.no)} className="text-slate-400 hover:text-rose-600" title="Kuru sil" data-testid={`camp-kur-del-${kur.no}`}><Trash2 size={15} /></button>
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1">
                      <span data-testid={`camp-kur-progress-${kur.no}`}>Hedef: {tgt} soru · Çözülen: {sv}</span><span>%{pct}</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>

                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                      Konular {!readOnly && <span className="normal-case font-medium">(tıkla: onayla / onayı kaldır)</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(kur.topics || []).map((t) => {
                        const dn = topicDone(kur.no, t);
                        return (
                          <button key={t} type="button" disabled={readOnly || locked} onClick={() => toggleTopic(kur.no, t)} data-testid={`camp-topic-${kur.no}-${t}`}
                            className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-semibold flex items-center gap-1 transition-colors ${dn ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-400 hover:text-indigo-700"} ${readOnly || locked ? "cursor-default" : "cursor-pointer"}`}>
                            {dn && <CheckCircle2 size={11} />}{t}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                      Kaynaklar {!readOnly && <span className="normal-case font-medium">(satıra tıkla: D/Y/B düzenle, ödev ver)</span>}
                    </div>
                    {res.length > 0 && (
                      <div className="overflow-x-auto rounded-lg border border-slate-200 mb-2">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50 text-xs">
                              <th className="px-3 py-2 font-medium">Kaynak</th>
                              <th className="px-3 py-2 font-medium">İçerik</th>
                              <th className="px-3 py-2 font-medium text-right">Toplam</th>
                              <th className="px-3 py-2 font-medium text-right">D</th>
                              <th className="px-3 py-2 font-medium text-right">Y</th>
                              <th className="px-3 py-2 font-medium text-right">B</th>
                              <th className="px-3 py-2 font-medium text-right">Net</th>
                              {!readOnly && <th className="px-3 py-2" />}
                            </tr>
                          </thead>
                          <tbody>
                            {res.map((r) => (
                              <tr key={r.id} className={`border-b border-slate-100 ${!readOnly ? "hover:bg-indigo-50/50 cursor-pointer" : ""}`}
                                onClick={() => !readOnly && setResModal({ ...r })} data-testid={`camp-res-${r.id}`}>
                                <td className="px-3 py-2 font-semibold text-slate-800">
                                  {r.name}
                                  {r.homework && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 align-middle">ÖDEV{r.homework_note ? `: ${r.homework_note}` : ""}</span>}
                                </td>
                                <td className="px-3 py-2 text-slate-600 text-xs">{r.content || "-"}</td>
                                <td className="px-3 py-2 text-right font-semibold">{r.total}</td>
                                <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{r.correct}</td>
                                <td className="px-3 py-2 text-right text-rose-700 font-semibold">{r.wrong}</td>
                                <td className="px-3 py-2 text-right text-slate-500">{r.blank}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">{Number(r.net || 0).toFixed(2)}</td>
                                {!readOnly && (
                                  <td className="px-3 py-2 text-right">
                                    <button onClick={(e) => { e.stopPropagation(); delResource(r.id); }} className="text-slate-300 hover:text-rose-600" data-testid={`camp-res-del-${r.id}`}><Trash2 size={14} /></button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!readOnly && !locked && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 items-end bg-slate-50 rounded-lg p-2.5 border border-slate-200">
                        <input className="ta-input !py-1.5 !text-xs col-span-2" placeholder="Kaynak adı" value={(addForms[kur.no] || blankRes).name}
                          onChange={(e) => setAddForms((p) => ({ ...p, [kur.no]: { ...(p[kur.no] || blankRes), name: e.target.value } }))} data-testid={`camp-res-name-${kur.no}`} />
                        <input className="ta-input !py-1.5 !text-xs col-span-2 sm:col-span-1" placeholder="İçerik (konu/sayfa)" value={(addForms[kur.no] || blankRes).content}
                          onChange={(e) => setAddForms((p) => ({ ...p, [kur.no]: { ...(p[kur.no] || blankRes), content: e.target.value } }))} data-testid={`camp-res-content-${kur.no}`} />
                        {["total", "correct", "wrong", "blank"].map((f) => (
                          <input key={f} type="number" className="ta-input !py-1.5 !text-xs text-center" placeholder={{ total: "Toplam", correct: "D", wrong: "Y", blank: "B" }[f]}
                            value={(addForms[kur.no] || blankRes)[f]}
                            onChange={(e) => setAddForms((p) => ({ ...p, [kur.no]: { ...(p[kur.no] || blankRes), [f]: e.target.value } }))} data-testid={`camp-res-${f}-${kur.no}`} />
                        ))}
                        <Button size="sm" onClick={() => addResource(kur.no)} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 h-8" data-testid={`camp-res-add-${kur.no}`}>
                          <Plus size={14} /><span className="ml-1">Ekle</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-violet-200 bg-violet-50 p-3" data-testid={`camp-exam-${kur.no}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-sm font-bold text-violet-800">
                        <Award size={15} /> {kur.exam_name || `${kur.no}. Kur Sınavı`}
                        <span className="text-xs font-semibold text-violet-500">({kur.exam_questions || 0} soru)</span>
                      </div>
                      {st?.exam_total > 0 && (
                        <div className="text-xs font-semibold text-violet-700" data-testid={`camp-exam-result-${kur.no}`}>
                          Sonuç: {st.exam_correct}D / {st.exam_wrong}Y / {st.exam_blank}B · Net {Number(st.exam_net || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                    {!readOnly && !locked && (
                      <div className="mt-2.5 flex flex-wrap items-end gap-2">
                        {["correct", "wrong", "blank"].map((f) => (
                          <div key={f}>
                            <label className="ta-label !mb-0.5">{{ correct: "Doğru", wrong: "Yanlış", blank: "Boş" }[f]}</label>
                            <input type="number" className="ta-input !py-1.5 !text-xs text-center w-20" value={ef[f] ?? (st ? st[`exam_${f}`] ?? "" : "")}
                              onChange={(e) => setExamForms((p) => ({ ...p, [kur.no]: { ...(p[kur.no] || {}), [f]: e.target.value } }))} data-testid={`camp-exam-${f}-${kur.no}`} />
                          </div>
                        ))}
                        <Button size="sm" variant="outline" onClick={() => saveKurExam(kur)} disabled={saving} className="h-8 border-violet-300 text-violet-700" data-testid={`camp-exam-save-${kur.no}`}>
                          <ClipboardList size={13} /><span className="ml-1">Sınavı Kaydet</span>
                        </Button>
                        <Button size="sm" onClick={() => togglePass(kur)} className={`h-8 ${done ? "bg-slate-500 hover:bg-slate-600" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid={`camp-pass-${kur.no}`}>
                          <CheckCircle2 size={13} /><span className="ml-1">{done ? "Geçişi Geri Al" : "Onayla — Sonraki Kura Geçsin"}</span>
                        </Button>
                      </div>
                    )}
                    {readOnly && !st?.exam_total && <div className="mt-1.5 text-xs text-violet-500">Sınav sonucu bekleniyor.</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {resModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setResModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="camp-res-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">Kaynak Düzenle / Ödev Ver</h3>
              <button onClick={() => setResModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="camp-res-modal-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="ta-label">Kaynak Adı</label>
                <input className="ta-input" value={resModal.name} onChange={(e) => setResModal({ ...resModal, name: e.target.value })} data-testid="camp-res-modal-name" />
              </div>
              <div className="col-span-2">
                <label className="ta-label">İçerik</label>
                <input className="ta-input" value={resModal.content || ""} onChange={(e) => setResModal({ ...resModal, content: e.target.value })} data-testid="camp-res-modal-content" />
              </div>
              {[["total", "Toplam Soru"], ["correct", "Doğru"], ["wrong", "Yanlış"], ["blank", "Boş"]].map(([f, l]) => (
                <div key={f}>
                  <label className="ta-label">{l}</label>
                  <input type="number" className="ta-input text-center" value={resModal[f]} onChange={(e) => setResModal({ ...resModal, [f]: e.target.value })} data-testid={`camp-res-modal-${f}`} />
                </div>
              ))}
              <div className="col-span-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <input type="checkbox" id="camp-hw" className="accent-amber-600" checked={!!resModal.homework} onChange={(e) => setResModal({ ...resModal, homework: e.target.checked })} data-testid="camp-res-modal-homework" />
                <label htmlFor="camp-hw" className="text-xs text-amber-800 font-semibold">Ödev olarak ver</label>
                <input className="ta-input !py-1 !text-xs flex-1" placeholder="Not (ops.)" value={resModal.homework_note || ""} onChange={(e) => setResModal({ ...resModal, homework_note: e.target.value })} data-testid="camp-res-modal-hwnote" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResModal(null)}>Vazgeç</Button>
              <Button onClick={saveResModal} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="camp-res-modal-save">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />}<span className="ml-1">Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {kurModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setKurModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="camp-kur-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{kurModal._new ? "Yeni Kur" : "Kuru Düzenle"}</h3>
              <button onClick={() => setKurModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="camp-kur-modal-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="ta-label">Kur No</label>
                <input type="number" min="1" className="ta-input" value={kurModal.no} onChange={(e) => setKurModal({ ...kurModal, no: e.target.value })} data-testid="camp-kur-no" />
              </div>
              <div>
                <label className="ta-label">Kur Adı</label>
                <input className="ta-input" value={kurModal.name} onChange={(e) => setKurModal({ ...kurModal, name: e.target.value })} data-testid="camp-kur-name" />
              </div>
              <div>
                <label className="ta-label">Hedef Soru Sayısı</label>
                <input type="number" min="0" className="ta-input" value={kurModal.target} onChange={(e) => setKurModal({ ...kurModal, target: e.target.value })} data-testid="camp-kur-target" />
              </div>
              <div>
                <label className="ta-label">Kur Sınavı Soru Sayısı</label>
                <input type="number" min="0" className="ta-input" value={kurModal.exam_questions} onChange={(e) => setKurModal({ ...kurModal, exam_questions: e.target.value })} data-testid="camp-kur-examq" />
              </div>
              <div className="col-span-2">
                <label className="ta-label">Kur Sınavı Adı</label>
                <input className="ta-input" value={kurModal.exam_name} onChange={(e) => setKurModal({ ...kurModal, exam_name: e.target.value })} data-testid="camp-kur-examname" />
              </div>
              <div className="col-span-2">
                <label className="ta-label">Konular (her satıra bir konu)</label>
                <textarea className="ta-input min-h-[140px] font-mono text-xs" value={kurModal.topicsText} onChange={(e) => setKurModal({ ...kurModal, topicsText: e.target.value })} data-testid="camp-kur-topics" placeholder={"Temel Kavramlar\nÜslü Sayılar\n..."} />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setKurModal(null)}>Vazgeç</Button>
              <Button onClick={saveKur} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="camp-kur-save">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />}<span className="ml-1">Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {campModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setCampModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="camp-new-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">Yeni Kamp</h3>
              <button onClick={() => setCampModal(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="ta-label">Kamp Adı</label>
                <input className="ta-input" value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} placeholder="Örn: TYT Fizik Kampı" data-testid="camp-new-name" />
              </div>
              <div>
                <label className="ta-label">Sınav Türü</label>
                <select className="ta-input" value={campForm.exam} onChange={(e) => setCampForm({ ...campForm, exam: e.target.value })} data-testid="camp-new-exam">
                  <option value="TYT">TYT</option>
                  <option value="AYT">AYT</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCampModal(false)}>Vazgeç</Button>
              <Button onClick={createCamp} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="camp-new-save">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Oluştur</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
